import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  BookOpen,
  Phone,
  Globe,
  ExternalLink,
  Heart,
  Brain,
  Users,
  Shield,
  FileText,
  LifeBuoy,
  Info,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Radio,
  Bell,
  Download,
  MessageCircle,
  Home,
  Search,
  ClipboardList
} from 'lucide-react';
import { Modal } from '../common/Modal';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { SearchInput } from '../common/SearchInput';
import { IncidentMap } from './IncidentMap';
import abcCatalogueData from '../../data/abc-emergency-catalogue.json';

interface Resource {
  id: string;
  name: string;
  description: string;
  website: string;
  phone?: string;
  category: 'emergency' | 'crisis' | 'support' | 'preparedness' | 'government';
  icon: React.ReactNode;
  color: string;
  textColor: string;
  additionalInfo?: string;
}

interface AbcBreadcrumb {
  label: string;
  url: string | null;
}

interface AbcPage {
  url: string;
  title: string;
  description: string;
  section: string | null;
  keywords: string[];
  headings: string[];
  breadcrumbs: AbcBreadcrumb[];
  contentSnippets: string[];
  articleSections: Array<{
    heading: string | null;
    body: string[];
  }>;
  publishedAt: string | null;
  modifiedAt: string | null;
}

interface AbcCatalogue {
  source: string;
  rootUrl: string;
  scrapedAt: string;
  totalPages: number;
  pages: AbcPage[];
}

const abcCatalogue = abcCatalogueData as AbcCatalogue;
const abcPagesData = abcCatalogue.pages;
const RESOURCE_CATEGORY_ORDER: Resource['category'][] = ['emergency', 'crisis', 'support', 'preparedness', 'government'];

// Article categories based on content
type ArticleCategory = 'bushfire' | 'cyclone' | 'flood' | 'storm' | 'preparedness' | 'recovery' | 'general';

const ARTICLE_CATEGORY_KEYWORDS: Record<ArticleCategory, string[]> = {
  bushfire: ['bushfire', 'fire', 'wildfire', 'afdrs', 'fire danger', 'fire rating', 'fire season', 'fire kit', 'fire safety'],
  cyclone: ['cyclone', 'hurricane', 'tropical', 'cyclone watch', 'cyclone warning', 'cyclone kit', 'cyclone season'],
  flood: ['flood', 'flooding', 'floodwater', 'flood warning', 'flash flood', 'river flood'],
  storm: ['storm', 'thunderstorm', 'supercell', 'hail', 'lightning', 'severe weather', 'power outage', 'outage'],
  preparedness: ['prepare', 'preparation', 'emergency kit', 'survival kit', 'plan', 'ready', 'preparedness', 'planning'],
  recovery: ['recover', 'recovery', 'after disaster', 'after emergency', 'rebuild', 'rebuilding'],
  general: []
};

function categorizeArticle(page: AbcPage): ArticleCategory {
  const searchText = [
    page.title,
    page.description,
    ...page.keywords,
    ...page.headings,
    ...page.contentSnippets
  ].join(' ').toLowerCase();

  // Check each category in order of specificity
  for (const [category, keywords] of Object.entries(ARTICLE_CATEGORY_KEYWORDS)) {
    if (category === 'general') continue;
    if (keywords.some(keyword => searchText.includes(keyword))) {
      return category as ArticleCategory;
    }
  }

  return 'general';
}

const abcSectionOptionsStatic = (() => {
  const sections = new Set<string>();
  let includeUncategorised = false;

  abcPagesData.forEach((page) => {
    if (page.section && page.section.trim()) {
      sections.add(page.section.trim());
    } else {
      includeUncategorised = true;
    }
  });

  const options = [
    { value: 'all', label: 'All sections' },
    ...Array.from(sections)
      .sort((a, b) => a.localeCompare(b))
      .map((section) => ({ value: section, label: section }))
  ];

  if (includeUncategorised) {
    options.push({ value: 'uncategorised', label: 'Uncategorised' });
  }

  return options;
})();

const ARTICLE_CATEGORY_LABELS: Record<ArticleCategory, string> = {
  bushfire: 'Bushfires',
  cyclone: 'Cyclones',
  flood: 'Floods',
  storm: 'Storms',
  preparedness: 'Preparedness',
  recovery: 'Recovery',
  general: 'General'
};

const ARTICLE_CATEGORY_COLORS: Record<ArticleCategory, { bg: string; text: string }> = {
  bushfire: { bg: 'bg-red-100', text: 'text-red-700' },
  cyclone: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  flood: { bg: 'bg-blue-100', text: 'text-blue-700' },
  storm: { bg: 'bg-purple-100', text: 'text-purple-700' },
  preparedness: { bg: 'bg-green-100', text: 'text-green-700' },
  recovery: { bg: 'bg-orange-100', text: 'text-orange-700' },
  general: { bg: 'bg-gray-100', text: 'text-gray-700' }
};

const ABC_EMERGENCY_URL = 'https://www.abc.net.au/emergency';
const ABC_INCIDENT_MAP_URL = 'https://www.abc.net.au/emergency/incidentsearch/australia';
const ABC_CYCLONE_PREP_URL = 'https://www.abc.net.au/emergency/prepare-for-cyclones/102645468';
const ABC_CYCLONE_KIT_URL = 'https://www.abc.net.au/emergency/what-you-need-in-your-cyclone-emergency-kit/102612154';
const ABC_AFDRS_URL = 'https://www.abc.net.au/emergency/bushfire-danger-ratings/12412040';
const ABC_BUSHFIRE_READY_URL = 'https://www.abc.net.au/emergency/get-ready-for-bushfire-season/102488106';
const ABC_BUSHFIRE_KIT_URL = 'https://www.abc.net.au/emergency/plan-for-emergency-survival-kit/12412048';
const ABC_AFTER_DISASTER_URL = 'https://www.abc.net.au/listen/programs/after-the-disaster';
const ABC_POWER_OUTAGE_URL = 'https://www.abc.net.au/emergency/dealing-with-a-power-outage-after-severe-weather/102608438';
const ABC_READY_TOGETHER_URL = 'https://www.redcross.org.au/getmedia/744dc2ab-9df6-4d60-9224-610074e1c17a/Stay-Connected-Toolkit.pdf.aspx';
const ABC_EMERGENCY_SERVICES_URL = 'https://www.abc.net.au/emergency/emergency-services-organisations-by-state/12461230';
const ABC_PHONE_SAFETY_URL = 'https://www.abc.net.au/emergency/how-your-phone-can-keep-you-safe-in-an-emergency/12830330';
const ABC_RECOVER_URL = 'https://www.abc.net.au/emergency/recover-after-an-emergency/12760566';

type HighlightItem = {
  text: string;
  url?: string;
};

type Highlight = {
  letter: 'A' | 'B' | 'C';
  title: string;
  description: string;
  items?: HighlightItem[];
};

const resources: Resource[] = [
  // Emergency Services
  {
    id: 'emergency-000',
    name: 'Emergency Services (000)',
    description: 'For immediate life-threatening emergencies. Call for police, fire, or ambulance services.',
    website: 'https://www.triplezero.gov.au/',
    phone: '000',
    category: 'emergency',
    icon: <Phone size={24} />,
    color: 'bg-red-50',
    textColor: 'text-red-600',
    additionalInfo: '000 is Australia\'s primary emergency number. Use it for life-threatening situations requiring immediate police, fire, or ambulance assistance.'
  },
  {
    id: 'abc-emergency',
    name: 'ABC Emergency',
    description: 'ABC Emergency centralises live warnings, preparedness guides, and recovery advice for incidents across Australia.',
    website: ABC_EMERGENCY_URL,
    category: 'preparedness',
    icon: <AlertTriangle size={24} />,
    color: 'bg-yellow-50',
    textColor: 'text-yellow-600',
    additionalInfo: 'Content reproduced from the Australian Broadcasting Corporation (ABC). See the highlights below for directly sourced guidance on alerts, preparedness, and community recovery.'
  },
  {
    id: 'lifeline',
    name: 'Lifeline',
    description: '24/7 crisis support and suicide prevention services. Available via phone, text, or online chat.',
    website: 'https://www.lifeline.org.au/',
    phone: '13 11 14',
    category: 'crisis',
    icon: <Heart size={24} />,
    color: 'bg-pink-50',
    textColor: 'text-pink-600',
    additionalInfo: 'Lifeline provides 24/7 crisis support and suicide prevention services. You can call, text, or chat online. All conversations are confidential and free.'
  },
  {
    id: 'beyond-blue',
    name: 'Beyond Blue',
    description: 'Australia\'s most well-known mental health organization. Get support for anxiety, depression, and suicide prevention.',
    website: 'https://www.beyondblue.org.au/',
    phone: '1300 22 4636',
    category: 'support',
    icon: <Brain size={24} />,
    color: 'bg-blue-50',
    textColor: 'text-blue-600',
    additionalInfo: 'Beyond Blue provides information and support to help everyone in Australia achieve their best possible mental health.'
  },
  {
    id: 'kids-helpline',
    name: 'Kids Helpline',
    description: '24/7 counselling for young people aged 5-25. Free, confidential, and anonymous support.',
    website: 'https://kidshelpline.com.au/',
    phone: '1800 55 1800',
    category: 'support',
    icon: <Users size={24} />,
    color: 'bg-purple-50',
    textColor: 'text-purple-600',
    additionalInfo: 'Kids Helpline is Australia\'s only free, confidential 24/7 online and phone counselling service for young people aged 5 to 25.'
  },
  {
    id: '1800respect',
    name: '1800RESPECT',
    description: '24/7 national sexual assault, domestic and family violence counselling service.',
    website: 'https://www.1800respect.org.au/',
    phone: '1800 737 732',
    category: 'crisis',
    icon: <Shield size={24} />,
    color: 'bg-rose-50',
    textColor: 'text-rose-600',
    additionalInfo: '1800RESPECT is the national sexual assault, domestic and family violence counselling service. It\'s a free, confidential 24/7 service.'
  },
  // Preparedness Resources
  {
    id: 'red-cross',
    name: 'Australian Red Cross',
    description: 'Emergency preparedness resources, first aid training, and disaster response information.',
    website: 'https://www.redcross.org.au/',
    phone: '1800 733 276',
    category: 'preparedness',
    icon: <Heart size={24} />,
    color: 'bg-red-50',
    textColor: 'text-red-600',
    additionalInfo: 'Red Cross provides emergency preparedness resources, first aid training, and supports communities during disasters.'
  },
  {
    id: 'ses',
    name: 'State Emergency Service (SES)',
    description: 'Emergency assistance for storms, floods, and other natural disasters. Available 24/7.',
    website: 'https://www.ses.vic.gov.au/',
    phone: '132 500',
    category: 'emergency',
    icon: <Shield size={24} />,
    color: 'bg-orange-50',
    textColor: 'text-orange-600',
    additionalInfo: 'SES provides emergency assistance for storms, floods, and other natural disasters. Call 132 500 for non-life-threatening emergencies.'
  },
  {
    id: 'bom',
    name: 'Bureau of Meteorology',
    description: 'Weather warnings, forecasts, and emergency weather information.',
    website: 'https://www.bom.gov.au/',
    category: 'preparedness',
    icon: <Globe size={24} />,
    color: 'bg-cyan-50',
    textColor: 'text-cyan-600',
    additionalInfo: 'The Bureau of Meteorology provides weather forecasts, warnings, and emergency weather information to help you prepare for severe weather events.'
  },
  // Government Resources
  {
    id: 'centrelink',
    name: 'Centrelink',
    description: 'Australian Government service providing financial support and services during emergencies.',
    website: 'https://www.servicesaustralia.gov.au/centrelink',
    phone: '132 307',
    category: 'government',
    icon: <FileText size={24} />,
    color: 'bg-indigo-50',
    textColor: 'text-indigo-600',
    additionalInfo: 'Centrelink delivers social security payments and services. Emergency assistance may be available during disasters.'
  },
  {
    id: 'ndis',
    name: 'NDIS',
    description: 'National Disability Insurance Scheme - support for people with disabilities during emergencies.',
    website: 'https://www.ndis.gov.au/',
    phone: '1800 800 110',
    category: 'government',
    icon: <LifeBuoy size={24} />,
    color: 'bg-teal-50',
    textColor: 'text-teal-600',
    additionalInfo: 'The NDIS provides funding to eligible people with disability. Emergency support may be available.'
  }
];

const ABC_SCRAPE_ISO = abcCatalogue.scrapedAt;

const abcEmergencyHighlights: Highlight[] = [
  {
    letter: 'A',
    title: 'Alerts & local warnings',
    description: 'Direct copy from ABC Emergency\'s location search and incident tools:',
    items: [
      {
        text: '“Enter a suburb or postcode in Australia and select from the list. Selecting a location will reload the page with your result.”',
        url: ABC_EMERGENCY_URL
      },
      {
        text: '“Incident Map” – national overview of current incidents, organised by state and territory.',
        url: ABC_INCIDENT_MAP_URL
      }
    ]
  },
  {
    letter: 'B',
    title: 'Be prepared',
    description: 'Latest preparedness guidance featured on ABC Emergency:',
    items: [
      { text: '“Are you prepared for cyclones?”', url: ABC_CYCLONE_PREP_URL },
      { text: '“What you need in your cyclone emergency kit”', url: ABC_CYCLONE_KIT_URL },
      { text: '“You need to know about the AFDRS”', url: ABC_AFDRS_URL },
      { text: '“Get ready for bushfire”', url: ABC_BUSHFIRE_READY_URL },
      { text: '“Build your bushfire kit”', url: ABC_BUSHFIRE_KIT_URL },
      { text: '“After The Disaster, a practical advice podcast”', url: ABC_AFTER_DISASTER_URL },
      { text: '“Dealing with a power outage after a storm”', url: ABC_POWER_OUTAGE_URL }
    ]
  },
  {
    letter: 'C',
    title: 'Connect & recover',
    description: 'Community connection and recovery resources sourced from ABC Emergency:',
    items: [
      { text: '“Stay connected with the #ReadyTogether toolkit”', url: ABC_READY_TOGETHER_URL },
      { text: '“Find emergency services where you are”', url: ABC_EMERGENCY_SERVICES_URL },
      { text: '“Your mobile phone could help save your life. Here\'s how”', url: ABC_PHONE_SAFETY_URL },
      { text: '“Helping you recover after an emergency”', url: ABC_RECOVER_URL }
    ]
  }
];

function formatDateLabel(value: string | null | undefined) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

const categoryLabels: Record<Resource['category'], string> = {
  'emergency': 'Emergency Services',
  'crisis': 'Crisis Support',
  'support': 'Support Services',
  'preparedness': 'Preparedness',
  'government': 'Government Services'
};

export function EmergencyResources() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<Resource['category'] | 'all'>('all');
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [selectedAbcSection, setSelectedAbcSection] = useState<string>('all');
  const [selectedAbcCategory, setSelectedAbcCategory] = useState<ArticleCategory | 'all'>('all');
  const [selectedAbcPage, setSelectedAbcPage] = useState<AbcPage | null>(null);
  const [activeView, setActiveView] = useState<'directory' | 'abc'>('directory');
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const articlesPerPage = 5;
  const contentPanelRef = useRef<HTMLDivElement | null>(null);

  const abcPages = abcPagesData;
  const abcSectionOptions = abcSectionOptionsStatic;

  const filteredResources = resources.filter(resource => {
    const matchesSearch = resource.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const filteredAbcPages = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    // Core website navigation pages to exclude (not actual articles)
    const excludedTitles = [
      'emergency advice',
      'readytogether',
      'find emergency services',
      'mobile phone could help save your life',
      'recover after an emergency',
      'find emergency services wherever you are',
      'your mobile phone could help save your life'
    ];
    
    // Language-related articles to exclude
    const languagePatterns = [
      'in your language',
      'translated',
      'auslan',
      'easy english',
      'information in your language',
      'preparation information in your language',
      'storm information in your language',
      'cyclone information in your language',
      'tsunami information in your language',
      'stay safe in storms and severe weather - translated',
      'stay safe during disasters - translated',
      'get ready for storms - translated',
      'get ready for emergencies in your language',
      'household emergency kit information - translated'
    ];

    return abcPages.filter((page) => {
      // Exclude the main ABC Emergency landing page
      if (page.url === 'https://www.abc.net.au/emergency') {
        return false;
      }

      // Exclude core website navigation pages
      const titleLower = page.title.toLowerCase();
      if (excludedTitles.some(excluded => titleLower.includes(excluded))) {
        return false;
      }
      
      // Exclude language-related articles
      if (languagePatterns.some(pattern => titleLower.includes(pattern))) {
        return false;
      }

      // Filter by category
      if (selectedAbcCategory !== 'all') {
        const articleCategory = categorizeArticle(page);
        if (articleCategory !== selectedAbcCategory) {
          return false;
        }
      }

      // Filter by section
      const matchesSection =
        selectedAbcSection === 'all' ||
        (selectedAbcSection === 'uncategorised' && (!page.section || !page.section.trim())) ||
        page.section === selectedAbcSection;

      if (!matchesSection) {
        return false;
      }

      // Enhanced search - check title, description, keywords, headings, and content snippets
      if (query) {
        const searchableText = [
          page.title,
          page.description,
          page.section ?? '',
          ...page.keywords,
          ...page.headings.slice(0, 5), // Limit headings to avoid noise
          ...page.contentSnippets.slice(0, 3) // Limit snippets to avoid noise
        ].join(' ').toLowerCase();

        // Split query into words for better matching
        const queryWords = query.split(/\s+/).filter(w => w.length > 2);
        if (queryWords.length > 0) {
          return queryWords.some(word => searchableText.includes(word));
        }
        return searchableText.includes(query);
      }

      return true;
    });
  }, [abcPages, searchQuery, selectedAbcSection, selectedAbcCategory]);

  const formattedScrapeDate = useMemo(() => formatDateLabel(ABC_SCRAPE_ISO), [ABC_SCRAPE_ISO]);
  
  // Pagination calculations
  const totalPages = Math.ceil(filteredAbcPages.length / articlesPerPage);
  const startIndex = (currentPage - 1) * articlesPerPage;
  const endIndex = startIndex + articlesPerPage;
  const paginatedAbcPages = useMemo(() => {
    return filteredAbcPages.slice(startIndex, endIndex);
  }, [filteredAbcPages, startIndex, endIndex]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedAbcSection, selectedAbcCategory]);

  const handleNextPage = useCallback(() => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  }, [totalPages]);

  const handlePrevPage = useCallback(() => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  }, []);

  const handleCall = useCallback((phone: string) => {
    window.location.href = `tel:${phone}`;
  }, []);

  const handleVisitWebsite = useCallback((url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const scrollToContentPanel = useCallback(() => {
    if (contentPanelRef.current) {
      contentPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const openKnowledgeBase = useCallback(() => {
    setActiveView('abc');
    requestAnimationFrame(() => {
      scrollToContentPanel();
    });
  }, [scrollToContentPanel]);

  const quickActions = useMemo(
    () => [
      {
        id: 'call-000',
        title: 'Emergency Services',
        subtitle: 'Triple Zero (000)',
        description: 'Life-threatening emergencies: police, fire, or ambulance',
        icon: <Phone size={24} />,
        color: 'bg-red-500',
        iconBg: 'bg-red-100',
        iconColor: 'text-red-600',
        urgent: true,
        onClick: () => handleCall('000')
      },
      {
        id: 'incident-map',
        title: 'Live Incident Map',
        subtitle: 'Real-time alerts',
        description: 'Active warnings and incidents across Australia',
        icon: <MapPin size={24} />,
        color: 'bg-orange-500',
        iconBg: 'bg-orange-100',
        iconColor: 'text-orange-600',
        onClick: () => setIsMapOpen(true)
      },
      {
        id: 'abc-knowledge',
        title: 'Knowledge Base',
        description: 'Preparedness guides, recovery advice, and alerts',
        icon: <BookOpen size={24} />,
        color: 'bg-blue-500',
        iconBg: 'bg-blue-100',
        iconColor: 'text-blue-600',
        onClick: openKnowledgeBase
      },
      {
        id: 'emergency-kit',
        title: 'Emergency Kit Checklist',
        subtitle: 'Be prepared',
        description: 'Essential items for your emergency survival kit',
        icon: <ClipboardList size={24} />,
        color: 'bg-green-500',
        iconBg: 'bg-green-100',
        iconColor: 'text-green-600',
        onClick: () => {
          setSearchQuery('emergency kit');
          setActiveView('abc');
          setSelectedAbcCategory('preparedness');
        }
      },
      {
        id: 'abc-radio',
        title: 'ABC Radio',
        subtitle: 'Emergency broadcasts',
        description: 'Listen to ABC Radio for emergency updates and warnings',
        icon: <Radio size={24} />,
        color: 'bg-purple-500',
        iconBg: 'bg-purple-100',
        iconColor: 'text-purple-600',
        onClick: () => handleVisitWebsite('https://www.abc.net.au/radio')
      },
      {
        id: 'search-resources',
        title: 'Search Resources',
        subtitle: 'Find help',
        description: 'Search emergency services and support in your area',
        icon: <Search size={24} />,
        color: 'bg-indigo-500',
        iconBg: 'bg-indigo-100',
        iconColor: 'text-indigo-600',
        onClick: () => {
          setActiveView('directory');
          scrollToContentPanel();
        }
      }
    ],
    [handleCall, handleVisitWebsite, openKnowledgeBase, scrollToContentPanel, setSearchQuery, setActiveView, setSelectedAbcCategory]
  );

  const stats = useMemo(
    () => [
      { label: 'Curated services', value: resources.length.toString() },
      { label: 'Articles', value: abcCatalogue.totalPages.toString() },
      { label: 'Last updated', value: formattedScrapeDate ?? 'Not available' }
    ],
    [formattedScrapeDate]
  );

const viewTabs = [
    {
      id: 'directory' as const,
      label: 'Resource Directory',
      description: 'Emergency services, crisis lines, and preparedness partners.'
    },
    {
      id: 'abc' as const,
    label: 'Knowledge Base',
    description: 'In-depth ABC Emergency articles, warnings, and recovery guidance.'
    }
  ];

  const resourcesByCategory = useMemo(() => {
    const groups = RESOURCE_CATEGORY_ORDER
      .map((categoryKey) => {
        const items = filteredResources.filter((resource) => resource.category === categoryKey);
        if (items.length === 0) {
          return null;
        }
        return {
          key: categoryKey,
          label: categoryLabels[categoryKey],
          items
        };
      })
      .filter(Boolean) as Array<{ key: Resource['category']; label: string; items: Resource[] }>;
    return groups;
  }, [filteredResources]);

  const searchPlaceholder =
    activeView === 'directory'
      ? 'Search services, descriptions, or phone numbers...'
      : 'Search ABC articles, incidents, and advice...';

  const categoryFilters: Array<Resource['category'] | 'all'> = ['all', ...RESOURCE_CATEGORY_ORDER];
  const directoryEmpty = activeView === 'directory' && filteredResources.length === 0;

  const meaningfulAbcSections = selectedAbcPage
    ? selectedAbcPage.articleSections
        .map((section) => {
          // Filter out social media button text from body
          const cleanedBody = section.body.filter((paragraph) => {
            const trimmed = paragraph.trim().toLowerCase();
            
            // Exact matches for social media button text
            if (trimmed === 'facebook' || 
                trimmed === 'twitter' || 
                trimmed === 'x (formerly twitter)' ||
                trimmed === 'x' ||
                trimmed === 'linkedin' ||
                trimmed === 'instagram' ||
                trimmed === 'share' ||
                trimmed === 'share on facebook' ||
                trimmed === 'share on twitter' ||
                trimmed === 'share on x' ||
                trimmed === 'share this') {
              return false;
            }
            
            // Contains social media keywords (but not as part of actual content)
            if (trimmed.startsWith('share on') || 
                trimmed.startsWith('share this') ||
                (trimmed.includes('facebook') && trimmed.length < 50) ||
                (trimmed.includes('twitter') && trimmed.length < 50) ||
                (trimmed.includes('x (formerly twitter)') && trimmed.length < 50)) {
              return false;
            }
            
            return true;
          });
          
          return {
            ...section,
            body: cleanedBody
          };
        })
        .filter((section) => {
          // Filter out overview and incident list sections
          const headingLower = (section.heading || '').toLowerCase();
          const bodyText = section.body.join(' ').toLowerCase();
          
          // Core website navigation sections to exclude
          const navigationPatterns = [
            'emergency advice',
            'readytogether',
            'ready together',
            'find emergency services',
            'mobile phone could help save your life',
            'recover after an emergency',
            'helping you recover'
          ];
          
          // Check if heading matches navigation patterns
          if (navigationPatterns.some(pattern => headingLower.includes(pattern))) {
            return false;
          }
          
          // Check if body text matches navigation patterns
          if (navigationPatterns.some(pattern => bodyText.includes(pattern))) {
            return false;
          }
          
          if (headingLower.includes('overview') || 
              headingLower.includes('active incidents') ||
              headingLower.includes('current incidents') ||
              headingLower.includes('incident list')) {
            return false;
          }
          
          if (bodyText.includes('active incidents') || 
              bodyText.includes('current incidents') ||
              (bodyText.includes('overview') && bodyText.length < 200)) {
            return false;
          }
          
          return (section.heading || section.body.length > 0);
        })
    : [];
  const articlePublishedLabel = selectedAbcPage ? formatDateLabel(selectedAbcPage.publishedAt) : null;
  const articleUpdatedLabel = selectedAbcPage ? formatDateLabel(selectedAbcPage.modifiedAt) : null;

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 md:p-10"
      >
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex w-14 h-14 rounded-xl bg-idecide-primary/10 items-center justify-center">
            <BookOpen size={32} className="text-idecide-primary" />
          </div>
          <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Knowledge Base</h1>
                  <p className="text-gray-600 mt-2">
                    Search iDecide’s emergency services directory and curated ABC Emergency articles in one view.
                  </p>
                  <ul className="mt-4 space-y-2 text-sm text-gray-600 list-disc list-inside">
                    <li>Locate dependable emergency, crisis, and community support services instantly.</li>
                    <li>Stay informed with ABC Emergency guidance for warnings, preparedness, and recovery.</li>
                    <li>Launch critical actions and deep-dive articles right from iDecide.</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 shadow-sm"
                >
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{stat.label}</p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">{stat.value}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Quick Actions Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-fr">
          {quickActions.map((action) => (
            <motion.button
              key={action.id}
              type="button"
              onClick={action.onClick}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              className={`group relative flex flex-col h-full min-h-[200px] w-full overflow-hidden rounded-2xl border-2 ${
                action.urgent 
                  ? 'border-red-300 bg-gradient-to-br from-red-50 to-red-100/50 shadow-lg hover:shadow-xl' 
                  : 'border-gray-200 bg-white shadow-sm hover:shadow-md'
              } p-6 text-left transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-idecide-primary focus:ring-offset-2`}
            >
              {/* Background gradient accent */}
              <div className={`absolute top-0 right-0 h-20 w-20 ${action.color} opacity-5 rounded-full -mr-10 -mt-10 transition-opacity group-hover:opacity-10`}></div>
              
              <div className="relative flex flex-col h-full">
                <div className="flex items-start gap-4 mb-3 flex-shrink-0">
                  <div className={`flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl ${action.iconBg} ${action.iconColor} shadow-sm transition-transform group-hover:scale-110`}>
                    {action.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2 mb-1 flex-wrap">
                      <h3 className={`text-base font-bold leading-tight ${action.urgent ? 'text-red-700' : 'text-gray-900'}`}>
                        {action.title}
                      </h3>
                      {action.urgent && (
                        <span className="inline-flex items-center rounded-full bg-red-500 px-2 py-0.5 text-xs font-bold text-white animate-pulse flex-shrink-0 mt-0.5">
                          URGENT
                        </span>
                      )}
                    </div>
                    {action.subtitle && (
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        {action.subtitle}
                      </p>
                    )}
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 leading-relaxed mb-3 line-clamp-2 flex-shrink-0">
                  {action.description}
                </p>
                
                {/* Action indicator - always at bottom */}
                <div className="mt-auto flex items-center text-xs font-medium text-gray-500 group-hover:text-idecide-primary transition-colors pt-2 border-t border-gray-100 flex-shrink-0">
                  <span>Tap to {action.id === 'call-000' ? 'call' : action.id === 'incident-map' ? 'view map' : action.id === 'abc-knowledge' ? 'browse' : 'open'}</span>
                  <ChevronRight size={14} className="ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </motion.button>
          ))}
        </div>

        <div ref={contentPanelRef} className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 md:p-8">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="w-full lg:max-w-xl">
            <SearchInput
                  placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={setSearchQuery}
            />
              </div>
              {searchQuery && (
                <Button variant="secondary" size="sm" onClick={() => setSearchQuery('')}>
                  Clear search
                </Button>
              )}
            </div>

            <div className="flex flex-wrap gap-2 rounded-2xl bg-gray-100 p-2">
              {viewTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveView(tab.id)}
                  className={`flex min-w-[200px] flex-1 flex-col rounded-xl px-4 py-3 text-left transition-colors ${
                    activeView === tab.id ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <span className="text-sm font-semibold">{tab.label}</span>
                  <span className="mt-1 text-xs text-gray-500">{tab.description}</span>
                </button>
              ))}
          </div>

            {activeView === 'directory' ? (
              <div className="space-y-6">
                <div className="flex flex-wrap gap-2">
                  {categoryFilters.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                      className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  selectedCategory === category
                          ? 'bg-idecide-primary text-white shadow'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                      {category === 'all' ? 'All resources' : categoryLabels[category]}
              </button>
            ))}
          </div>

                {directoryEmpty ? (
                  <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-600">
                    No services match this search right now. Try another keyword or switch to the knowledge base.
                  </div>
                ) : (
                  <div className="space-y-10">
                    {resourcesByCategory.map((group) => (
                      <div key={group.key} className="space-y-4">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between">
                          <h3 className="text-xl font-semibold text-gray-900">{group.label}</h3>
                          <span className="text-sm text-gray-500">{group.items.length} resources</span>
                        </div>
                        <div className="grid gap-4 md:grid-cols-2">
                          {group.items.map((resource) => (
                            <Card
                              key={resource.id}
                              className="h-full border border-gray-200 bg-white transition-all hover:shadow-md"
                            >
                              <div className="flex flex-col gap-4 p-6">
                                <div className="flex items-start gap-4">
                                  <div className={`flex h-12 w-12 items-center justify-center rounded-lg ${resource.color}`}>
                                    <div className={resource.textColor}>{resource.icon}</div>
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                      {categoryLabels[resource.category]}
                                    </p>
                                    <h4 className="text-lg font-semibold text-gray-900">{resource.name}</h4>
                                    <p className="mt-1 text-sm text-gray-600">{resource.description}</p>
        </div>
      </div>

                                {resource.additionalInfo && (
                                  <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 text-sm text-gray-600">
                                    {resource.additionalInfo}
                                  </div>
                                )}

                                <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-gray-600">
                                  {resource.phone && (
                                    <span className="flex items-center gap-1">
                                      <Phone size={16} className="text-gray-500" />
                                      {resource.phone}
                                    </span>
                                  )}
                                  <span className="flex items-center gap-1">
                                    <Globe size={16} className="text-gray-500" />
                                    {resource.website.replace(/^https?:\/\//, '')}
                                  </span>
          </div>

                                <div className="flex flex-wrap gap-2">
                                  <Button
                                    variant="primary"
                                    size="sm"
                  onClick={() => setSelectedResource(resource)}
                >
                                    View details
                                  </Button>
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => handleVisitWebsite(resource.website)}
                                  >
                                    Visit site
                                  </Button>
                                  {resource.phone && (
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      onClick={() => handleCall(resource.phone!)}
                                    >
                                      Call now
                                    </Button>
                                  )}
                                  {resource.id === 'abc-emergency' && (
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      onClick={openKnowledgeBase}
                                    >
                                      Browse Knowledge Base
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                <div className="grid gap-3 sm:grid-cols-3">
                  {abcEmergencyHighlights.map((highlight) => (
                    <div
                      key={highlight.letter}
                      className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm space-y-3"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-idecide-primary/10 text-sm font-semibold text-idecide-primary">
                          {highlight.letter}
                    </div>
                        <h4 className="text-sm font-semibold text-gray-900">{highlight.title}</h4>
                    </div>
                      <p className="text-sm text-gray-600">{highlight.description}</p>
                      {highlight.items && (
                        <ul className="space-y-1 text-xs text-gray-600">
                          {highlight.items.slice(0, 2).map((item, index) => (
                            <li key={`${highlight.letter}-${index}`}>
                              {item.text}
                              {item.url && (
                                <a
                                  href={item.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ml-1 text-idecide-primary hover:underline"
                                >
                                  Source
                                </a>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                    </div>

                {/* Category Filters */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">Filter by Category</label>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {(['all', 'bushfire', 'cyclone', 'flood', 'storm', 'preparedness', 'recovery', 'general'] as const).map((category) => (
                      <button
                        key={category}
                        onClick={() => setSelectedAbcCategory(category)}
                        className={`rounded-lg px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${
                          selectedAbcCategory === category
                            ? 'bg-idecide-primary text-white shadow'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {category === 'all' ? 'All Categories' : ARTICLE_CATEGORY_LABELS[category]}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Section Filters */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">Filter by Section</label>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {abcSectionOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setSelectedAbcSection(option.value)}
                        className={`rounded-lg px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${
                          selectedAbcSection === option.value
                            ? 'bg-idecide-primary text-white shadow'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {paginatedAbcPages.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-600">
                    No ABC Emergency articles match this search yet. Adjust keywords or section filters.
                  </div>
                ) : (
                  <>
                    <div className="space-y-4">
                      {paginatedAbcPages.map((page) => {
                        const publishedLabel = formatDateLabel(page.publishedAt);
                        const updatedLabel = formatDateLabel(page.modifiedAt);
                        return (
                          <div
                            key={page.url}
                            className="rounded-xl border border-gray-200 bg-gray-50 p-5 md:p-6 space-y-4 hover:border-idecide-primary/50 transition-colors"
                          >
                            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                              <div className="space-y-3 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  {(() => {
                                    const category = categorizeArticle(page);
                                    const colors = ARTICLE_CATEGORY_COLORS[category];
                                    return (
                                      <span className={`inline-flex items-center rounded-full ${colors.bg} ${colors.text} px-3 py-1 text-xs font-semibold border border-current/20`}>
                                        {ARTICLE_CATEGORY_LABELS[category]}
                                      </span>
                                    );
                                  })()}
                                  {page.section?.trim() && (
                                    <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600 border border-gray-200">
                                      {page.section.trim()}
                                    </span>
                                  )}
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900">{page.title}</h3>
                                <p className="text-xs text-gray-500">
                                  Source:{' '}
                                  <a
                                    href={page.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-idecide-primary hover:underline"
                                  >
                                    {page.url.replace(/^https?:\/\//, '')}
                                  </a>
                                </p>
                                <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                                  {page.description}
                                </p>
                                {(publishedLabel || updatedLabel) && (
                                  <div className="text-xs text-gray-500 space-x-3">
                                    {publishedLabel && <span>Published {publishedLabel}</span>}
                                    {updatedLabel && <span>Updated {updatedLabel}</span>}
                                  </div>
                                )}
                                {page.keywords.length > 0 && (
                                  <div className="flex flex-wrap gap-2">
                                    {page.keywords.slice(0, 6).map((keyword) => (
                                      <span
                                        key={keyword}
                                        className="inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-medium text-gray-600 border border-gray-200"
                                      >
                                        {keyword}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="flex sm:self-start">
                                <Button variant="primary" size="sm" onClick={() => setSelectedAbcPage(page)}>
                                  Read on iDecide
                                </Button>
                              </div>
                            </div>

                            {/* Only show meaningful headings (filter out navigation/boilerplate) */}
                            {page.headings.length > 0 && (() => {
                              const meaningfulHeadings = page.headings.filter(h => {
                                const hLower = h.toLowerCase();
                                return !hLower.includes('emergency advice') &&
                                       !hLower.includes('readytogether') &&
                                       !hLower.includes('find emergency services') &&
                                       !hLower.includes('mobile phone') &&
                                       !hLower.includes('recover after') &&
                                       !hLower.includes('app promotion') &&
                                       !hLower.includes('in the news') &&
                                       h.length > 10; // Filter out very short headings
                              }).slice(0, 3);
                              
                              return meaningfulHeadings.length > 0 ? (
                                <div>
                                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Key sections</h4>
                                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                                    {meaningfulHeadings.map((heading) => (
                                      <li key={heading}>{heading}</li>
                                    ))}
                                  </ul>
                                </div>
                              ) : null;
                            })()}
                          </div>
                        );
                      })}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                        <div className="text-sm text-gray-600">
                          Showing {startIndex + 1} to {Math.min(endIndex, filteredAbcPages.length)} of {filteredAbcPages.length} articles
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={handlePrevPage}
                            disabled={currentPage === 1}
                            className="flex items-center gap-2"
                          >
                            <ChevronLeft size={16} />
                            Previous
                          </Button>
                          <div className="px-4 py-2 text-sm font-medium text-gray-700">
                            Page {currentPage} of {totalPages}
                          </div>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages}
                            className="flex items-center gap-2"
                          >
                            Next
                            <ChevronRight size={16} />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Resource Detail Modal */}
      <Modal
        isOpen={!!selectedResource}
        onClose={() => setSelectedResource(null)}
        title={selectedResource?.name || ''}
        maxWidth="xl"
      >
        {selectedResource && (
          <div className="space-y-6">
            {/* Category Badge */}
            <div>
              <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                {categoryLabels[selectedResource.category]}
              </span>
            </div>

            {/* Description */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <Info size={16} />
                Description
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {selectedResource.description}
              </p>
            </div>

            {/* Additional Info */}
            {selectedResource.additionalInfo && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Info size={16} />
                  Additional Information
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {selectedResource.additionalInfo}
                </p>
              </div>
            )}

            {/* Contact Information */}
            <div className="space-y-4 pt-4 border-t border-gray-200">
              {selectedResource.phone && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <Phone size={16} />
                    Phone Number
                  </h3>
                  <div className="flex items-center gap-3">
                    <p className="text-lg font-medium text-gray-900">{selectedResource.phone}</p>
                    <Button
                      variant="primary"
                      onClick={() => handleCall(selectedResource.phone!)}
                      className="flex items-center gap-2"
                    >
                      <Phone size={16} />
                      Call Now
                    </Button>
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Globe size={16} />
                  Website
                </h3>
                <div className="flex items-center gap-3">
                  <p className="text-gray-600 break-all">{selectedResource.website}</p>
                  <Button
                    variant="primary"
                    onClick={() => handleVisitWebsite(selectedResource.website)}
                    className="flex items-center gap-2 whitespace-nowrap"
                  >
                    <ExternalLink size={16} />
                    Visit Website
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ABC Article Modal */}
      <Modal
        isOpen={!!selectedAbcPage}
        onClose={() => setSelectedAbcPage(null)}
        title={selectedAbcPage?.title || ''}
        maxWidth="6xl"
      >
        {selectedAbcPage && (
          <div className="space-y-6 max-h-[80vh] overflow-y-auto pr-2">
            {/* Header Card */}
            <div className="bg-gradient-to-br from-idecide-primary/10 via-blue-50 to-purple-50 rounded-xl p-6 border-2 border-idecide-primary/20 shadow-lg relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-idecide-primary/5 rounded-full -mr-16 -mt-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-200/20 rounded-full -ml-12 -mb-12"></div>
              <div className="relative z-10">
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="text-xs text-gray-500 mb-2">
                      Source:{' '}
                      <a
                        href={selectedAbcPage.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-idecide-primary hover:underline"
                      >
                        {selectedAbcPage.url.replace(/^https?:\/\//, '')}
                      </a>
                    </div>
                    <p className="text-gray-700 leading-relaxed text-base">{selectedAbcPage.description}</p>
                  </div>
                </div>
                
                <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-gray-300">
                  {(articlePublishedLabel || articleUpdatedLabel) && (
                    <div className="text-xs text-gray-600 space-x-3">
                      {articlePublishedLabel && (
                        <span className="inline-flex items-center gap-1">
                          <span className="font-medium">Published:</span> {articlePublishedLabel}
                        </span>
                      )}
                      {articleUpdatedLabel && (
                        <span className="inline-flex items-center gap-1">
                          <span className="font-medium">Updated:</span> {articleUpdatedLabel}
                        </span>
                      )}
                    </div>
                  )}
                  {selectedAbcPage.keywords.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {selectedAbcPage.keywords.slice(0, 8).map((keyword) => (
                        <span
                          key={keyword}
                          className="inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-medium text-gray-700 border border-gray-300 shadow-sm"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              </div>
            </div>

            {/* Article Sections */}
            <div className="space-y-4">
              {meaningfulAbcSections.map((section, sectionIndex) => {
                // Color scheme rotation for visual variety
                const colorSchemes = [
                  { bg: 'bg-gradient-to-br from-blue-50 to-cyan-50', border: 'border-blue-200', accent: 'text-blue-600', iconBg: 'bg-blue-100' },
                  { bg: 'bg-gradient-to-br from-green-50 to-emerald-50', border: 'border-green-200', accent: 'text-green-600', iconBg: 'bg-green-100' },
                  { bg: 'bg-gradient-to-br from-purple-50 to-pink-50', border: 'border-purple-200', accent: 'text-purple-600', iconBg: 'bg-purple-100' },
                  { bg: 'bg-gradient-to-br from-orange-50 to-amber-50', border: 'border-orange-200', accent: 'text-orange-600', iconBg: 'bg-orange-100' },
                  { bg: 'bg-gradient-to-br from-indigo-50 to-violet-50', border: 'border-indigo-200', accent: 'text-indigo-600', iconBg: 'bg-indigo-100' },
                ];
                const colorScheme = colorSchemes[sectionIndex % colorSchemes.length];
                
                return (
                <div 
                  key={`${selectedAbcPage.url}-section-${sectionIndex}`} 
                  className={`${colorScheme.bg} rounded-xl border-2 ${colorScheme.border} p-6 shadow-md hover:shadow-lg transition-all duration-300 relative overflow-hidden`}
                >
                  <div className="absolute top-0 right-0 w-20 h-20 opacity-10">
                    <div className={`w-full h-full ${colorScheme.iconBg} rounded-full -mr-10 -mt-10`}></div>
                  </div>
                  {section.heading && (
                    <div className="flex items-center gap-3 mb-4 pb-3 border-b-2 border-gray-300/50">
                      <div className={`${colorScheme.iconBg} rounded-lg p-2`}>
                        <FileText size={20} className={colorScheme.accent} />
                      </div>
                      <h4 className={`text-xl font-bold ${colorScheme.accent} flex-1`}>
                        {section.heading}
                      </h4>
                    </div>
                  )}
                  <div className="space-y-3">
                    {section.body.map((paragraph, paragraphIndex) => {
                      // Check if it's a list item (starts with bullet or number)
                      const isListItem = paragraph.trim().startsWith('•') || paragraph.match(/^\d+\.\s/);
                      const isBlockquote = paragraph.startsWith('"') && paragraph.endsWith('"');
                      
                      if (isListItem && !isBlockquote) {
                        return (
                          <div
                            key={`${selectedAbcPage.url}-section-${sectionIndex}-p-${paragraphIndex}`}
                            className="flex items-start gap-3 pl-4 bg-white/60 rounded-lg p-3 my-2"
                          >
                            <div className={`${colorScheme.iconBg} rounded-full p-1 mt-0.5`}>
                              <span className={`${colorScheme.accent} font-bold text-lg`}>•</span>
                            </div>
                            <p className="text-gray-800 leading-relaxed flex-1 font-medium">
                              {paragraph.replace(/^[•\d+\.]\s*/, '').trim()}
                            </p>
                          </div>
                        );
                      }
                      
                      if (isBlockquote) {
                        return (
                          <div
                            key={`${selectedAbcPage.url}-section-${sectionIndex}-p-${paragraphIndex}`}
                            className={`bg-white/80 border-l-4 ${colorScheme.border} pl-5 py-4 rounded-r-lg my-3 shadow-sm`}
                          >
                            <div className="flex items-start gap-2">
                              <Info size={18} className={`${colorScheme.accent} mt-0.5 flex-shrink-0`} />
                              <p className={`${colorScheme.accent} leading-relaxed italic font-medium`}>
                                {paragraph.replace(/^["']|["']$/g, '').trim()}
                              </p>
                            </div>
                          </div>
                        );
                      }
                      
                      return (
                        <div
                          key={`${selectedAbcPage.url}-section-${sectionIndex}-p-${paragraphIndex}`}
                          className="bg-white/50 rounded-lg p-4 my-2 border border-white/80"
                        >
                          <p className="text-gray-800 leading-relaxed text-base">
                            {paragraph}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
              })}
              {meaningfulAbcSections.length === 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                  <p className="text-sm text-yellow-800">
                    Full article content was not available in the latest scrape. Please open the source article on ABC for complete details.
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl p-5 border-2 border-gray-300 shadow-inner">
              <div className="flex items-center justify-center gap-2">
                <Shield size={16} className="text-gray-600" />
                <p className="text-xs text-gray-700 text-center font-medium">
                  Content reproduced from the Australian Broadcasting Corporation (ABC). Refer to the source link above for the latest updates.
                </p>
                <Shield size={16} className="text-gray-600" />
              </div>
            </div>
          </div>
        )}
      </Modal>

      <IncidentMap isOpen={isMapOpen} onClose={() => setIsMapOpen(false)} />
    </div>
  );
}

